import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createDaisyPayoutRegister } from '../../../api/createDaisyPayoutRegister';
// IMPORT THE ENUMS HERE
import { DaisyPayoutRegister, CurrencyType, NetworkType } from '../../../models/daisypayoutregister.model';

interface DaisyPayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: number;
}

export default function DaisyPayoutModal({ isOpen, onClose, onSuccess, userId }: DaisyPayoutModalProps) {
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    network: NetworkType.TRC, // Default using Enum
    currency: CurrencyType.USDT, // Default using Enum
    amount: '',
    wallet: "",
    hash: ''
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        network: NetworkType.TRC,
        currency: CurrencyType.USDT,
        amount: '',
        wallet: '',
        hash: ''
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || isNaN(Number(formData.amount))) {
      toast.error("Kérlek adj meg egy érvényes összeget!");
      return;
    }
    if (!formData.hash) {
      toast.error("A tranzakciós hash megadása kötelező!");
      return;
    }

    setLoading(true);
    try {
      // The API expects the payload to match the interface using the Enums
      await createDaisyPayoutRegister({
        user_id: userId,
        date: new Date(formData.date),
        network: formData.network,
        currency: formData.currency,
        wallet: formData.wallet,
        amount: Number(formData.amount),
        hash: formData.hash
      });

      toast.success("Kifizetés sikeresen rögzítve!");
      onSuccess(); 
      onClose();   
    } catch (error) {
      console.error(error);
      toast.error("Hiba történt a mentés során.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            aria-hidden="true"
            onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Új kifizetés rögzítése
                </h3>
                
                <form id="payout-form" onSubmit={handleSubmit} className="mt-4 space-y-4">
                  
                  {/* Date Picker */}
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700">Dátum</label>
                    <input
                      type="date"
                      name="date"
                      id="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brown focus:border-brown sm:text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                      {/* Network Dropdown */}
                      <div>
                          <label htmlFor="network" className="block text-sm font-medium text-gray-700">Hálózat</label>
                          <select
                              id="network"
                              name="network"
                              value={formData.network}
                              onChange={(e) => setFormData({...formData, network: e.target.value as NetworkType})}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brown focus:border-brown sm:text-sm rounded-md"
                          >
                              {Object.values(NetworkType).map((net) => (
                                  <option key={net} value={net}>{net}</option>
                              ))}
                          </select>
                      </div>

                      {/* Currency Dropdown */}
                      <div>
                          <label htmlFor="currency" className="block text-sm font-medium text-gray-700">Deviza</label>
                          <select
                              id="currency"
                              name="currency"
                              value={formData.currency}
                              onChange={(e) => setFormData({...formData, currency: e.target.value as CurrencyType})}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brown focus:border-brown sm:text-sm rounded-md"
                          >
                              {Object.values(CurrencyType).map((curr) => (
                                  <option key={curr} value={curr}>{curr}</option>
                              ))}
                          </select>
                      </div>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Összeg</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="number"
                        name="amount"
                        id="amount"
                        required
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        className="focus:ring-brown focus:border-brown block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md py-2"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">
                          {formData.currency}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Textfield */}
                  <div>
                    <label htmlFor="wallet" className="block text-sm font-medium text-gray-700">Tárcacím</label>
                    <input
                      type="text"
                      name="wallet"
                      id="wallet"
                      required
                      placeholder="Wallet érték"
                      value={formData.wallet}
                      onChange={(e) => setFormData({...formData, wallet: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brown focus:border-brown sm:text-sm font-mono"
                    />
                  </div>


                  {/* Hash Textfield */}
                  <div>
                    <label htmlFor="hash" className="block text-sm font-medium text-gray-700">Tranzakciós Hash</label>
                    <input
                      type="text"
                      name="hash"
                      id="hash"
                      required
                      placeholder="Hash érték"
                      value={formData.hash}
                      onChange={(e) => setFormData({...formData, hash: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brown focus:border-brown sm:text-sm font-mono"
                    />
                  </div>

                  

                </form>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-dark-blue text-base font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Mentés...' : 'Rögzítés'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Mégse
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}